import React from "react";
import {
  Modal,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
} from "@mui/material";

interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: React.ReactNode;
  confirmButtonText?: string;
  cancelButtonText?: string;
  themeColor?: "primary" | "error" | "warning" | "success";
}

const themeColors = {
  primary: {
    main: "rgba(64, 144, 255, 1)",
    border: "rgba(115, 169, 244, 1)",
  },
  error: {
    main: "rgba(255, 82, 82, 1)",
    border: "rgba(255, 82, 82, 1)",
  },
  warning: {
    main: "rgba(255, 145, 55, 1)",
    border: "rgba(252, 174, 97, 1)",
  },
  success: {
    main: "rgb(50, 225, 170)",
    border: "rgb(50, 225, 170)",
  },
};

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmButtonText = "OK",
  cancelButtonText = "キャンセル",
  themeColor = "primary",
}) => {
  const color = themeColors[themeColor];

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="confirmation-modal-title">
      <Card
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 450,
          p: 3,
          borderRadius: "12px",
          outline: "none",
          "::before": {
            content: '""',
            position: "absolute",
            inset: "4px",
            border: `2px solid ${color.border}`,
            pointerEvents: "none",
            borderRadius: "12px",
          },
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: 0,
              mb: 2,
              pb: 1,
              borderBottom: `2px solid ${color.border}`,
              width: "calc(100% - 2px)",
              mx: "auto",
            }}
          >
            <Typography
              id="confirmation-modal-title"
              variant="h6"
              component="h2"
              gutterBottom
              sx={{ textAlign: "center", color: color.main, fontWeight: 'bold' }}
            >
              {title}
            </Typography>
          </Box>
          <Typography sx={{ mt: 2, px: 1 }}>{message}</Typography>
        </CardContent>
        <CardActions sx={{ justifyContent: "flex-end", p: 0, pt: 3, gap: 2 }}>
          <Button
            onClick={handleConfirm}
            variant="contained"
            sx={{
              width: 110,
              whiteSpace: "nowrap",
              fontWeight: "bold",
              fontSize: "16px",
              backgroundColor: color.main,
              border: "4px solid rgba(240, 232, 249, 0.9)",
              borderRadius: "25px",
              boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.3)",
              "&:hover": {
                backgroundColor: color.main,
              },
            }}
          >
            {confirmButtonText}
          </Button>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              width: 110,
              whiteSpace: "nowrap",
              fontWeight: "bold",
              fontSize: "16px",
              color: "#fff",
              backgroundColor: "#9e9e9e",
              border: "4px solid rgba(240, 232, 249, 0.9)",
              borderRadius: "25px",
              boxShadow: "4px 4px 8px rgba(0, 0, 0, 0.3)",
              "&:hover": {
                backgroundColor: "#9e9e9e",
              },
            }}
          >
            {cancelButtonText}
          </Button>
        </CardActions>
      </Card>
    </Modal>
  );
};

export default ConfirmationModal;
